import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faDownload,
  faFilter,
  faSort,
  faFileExcel,
  faChartBar,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { calculateBusinessHours, KELLANOVA_THRESHOLDS } from "../utils/businessHours";

const DrillDownModal = ({
  isOpen,
  onClose,
  data,
  filterConfig,
  title = "Drill Down Results",
}) => {
  const [sortConfig, setSortConfig] = useState({ field: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBracket, setSelectedBracket] = useState("all"); // all, green, yellow, red
  const [selectedEntryPoint, setSelectedEntryPoint] = useState("all"); // all or specific entry point
  const itemsPerPage = 20;

  // Reset filters when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setSelectedBracket("all");
      setSelectedEntryPoint(filterConfig?.entryPoint || "all");
      setCurrentPage(1);
    }
  }, [isOpen, filterConfig]);

  // Process and filter data based on user selections
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    return data.map((item) => {
      // Calculate attention time for all records
      const attentionTime = calculateBusinessHours(
        new Date(item.header?.detail?.dates_data?.date_init),
        new Date(item.header?.detail?.dates_data?.date_crossing)
      );

      return {
        ...item,
        calculatedAttentionTime: attentionTime,
      };
    });
  }, [data]);

  // Filter data based on user selections
  const filteredData = useMemo(() => {
    let filtered = [...processedData];

    // Filter by entry point
    if (selectedEntryPoint !== "all") {
      filtered = filtered.filter((item) => {
        const itemEntryPoint = item.header?.pedimento?.pedimento_entry_point;
        return itemEntryPoint === selectedEntryPoint;
      });
    }

    // Filter by performance bracket
    if (selectedBracket !== "all") {
      filtered = filtered.filter((item) => {
        // Exclude records with null attention time from bracket filtering
        if (item.calculatedAttentionTime === null) {
          return false;
        }
        
        const attentionTime = item.calculatedAttentionTime;
        
        switch (selectedBracket) {
          case "green":
            return attentionTime <= KELLANOVA_THRESHOLDS.good;
          case "yellow":
            return (
              attentionTime > KELLANOVA_THRESHOLDS.good &&
              attentionTime <= KELLANOVA_THRESHOLDS.moderate
            );
          case "red":
            return attentionTime > KELLANOVA_THRESHOLDS.moderate;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [processedData, selectedBracket, selectedEntryPoint]);

  // Calculate statistics for filter buttons
  const statistics = useMemo(() => {
    const validRecords = processedData.filter(item => item.calculatedAttentionTime !== null);
    
    const green = validRecords.filter(item => 
      item.calculatedAttentionTime <= KELLANOVA_THRESHOLDS.good
    ).length;
    
    const yellow = validRecords.filter(item => 
      item.calculatedAttentionTime > KELLANOVA_THRESHOLDS.good &&
      item.calculatedAttentionTime <= KELLANOVA_THRESHOLDS.moderate
    ).length;
    
    const red = validRecords.filter(item => 
      item.calculatedAttentionTime > KELLANOVA_THRESHOLDS.moderate
    ).length;

    return {
      all: processedData.length,
      green,
      yellow,
      red
    };
  }, [processedData]);

  // Get unique entry points for dropdown
  const entryPointOptions = useMemo(() => {
    const uniquePoints = new Set();
    processedData.forEach(item => {
      const entryPoint = item.header?.pedimento?.pedimento_entry_point;
      if (entryPoint) {
        uniquePoints.add(entryPoint);
      }
    });
    return Array.from(uniquePoints).sort();
  }, [processedData]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.field) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.field) {
        case "reference":
          aValue = a.header?.reference || "";
          bValue = b.header?.reference || "";
          break;
        case "customer":
          aValue = a.header?.customer_name || "";
          bValue = b.header?.customer_name || "";
          break;
        case "attentionTime":
          aValue = a.calculatedAttentionTime || 0;
          bValue = b.calculatedAttentionTime || 0;
          break;
        case "entryPoint":
          aValue = a.header?.pedimento?.pedimento_entry_point || "";
          bValue = b.header?.pedimento?.pedimento_entry_point || "";
          break;
        case "status":
          aValue = a.header?.detail?.traffic_data?.status || "";
          bValue = b.header?.detail?.traffic_data?.status || "";
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Handle sorting
  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Get performance indicator
  const getPerformanceIndicator = (attentionTime) => {
    if (attentionTime === null || attentionTime === undefined) {
      return { icon: faClock, color: "text-gray-500", text: "N/A" };
    }

    if (attentionTime <= KELLANOVA_THRESHOLDS.good) {
      return { icon: faCheckCircle, color: "text-green-600", text: "Good" };
    } else if (attentionTime <= KELLANOVA_THRESHOLDS.moderate) {
      return { icon: faExclamationTriangle, color: "text-yellow-600", text: "Moderate" };
    } else {
      return { icon: faExclamationTriangle, color: "text-red-600", text: "Poor" };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Reference",
      "Customer Name",
      "Entry Point",
      "Status",
      "Date Init",
      "Date Crossing",
      "Attention Time (Business Hours)",
      "Performance"
    ];

    const csvData = sortedData.map((item) => {
      const performance = getPerformanceIndicator(item.calculatedAttentionTime);
      return [
        item.header?.reference || "",
        item.header?.customer_name || "",
        item.header?.pedimento?.pedimento_entry_point || "",
        item.header?.detail?.traffic_data?.status || "",
        formatDate(item.header?.detail?.dates_data?.date_init),
        formatDate(item.header?.detail?.dates_data?.date_crossing),
        item.calculatedAttentionTime ? item.calculatedAttentionTime.toFixed(1) : "N/A",
        performance.text,
      ];
    });

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(","))
    ].join("\\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      // Create filename based on active filters
      const filters = [];
      if (selectedBracket !== "all") filters.push(selectedBracket);
      if (selectedEntryPoint !== "all") filters.push(`ep${selectedEntryPoint}`);
      const filterSuffix = filters.length > 0 ? `-${filters.join("-")}` : "";
      
      link.setAttribute(
        "download",
        `performance-analysis${filterSuffix}-${Date.now()}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!isOpen) return null;

  // Create modal title
  const getModalTitle = () => {
    return "Performance Analysis";
  };

  // Handle filter changes
  const handleBracketChange = (bracket) => {
    setSelectedBracket(bracket);
    setCurrentPage(1); // Reset to first page
  };

  const handleEntryPointChange = (entryPoint) => {
    setSelectedEntryPoint(entryPoint);
    setCurrentPage(1); // Reset to first page
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-11/12 h-5/6 max-w-7xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 bg-gradient-to-r from-[#1A2654] to-blue-700 text-white">
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={faChartBar}
              className="mr-3 text-blue-600 bg-white rounded-full p-2"
            />
            <div>
              <h3 className="text-xl font-semibold">{getModalTitle()}</h3>
              <p className="text-blue-100 text-sm mt-1">
                Showing {filteredData.length} of {statistics.all} records
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors text-sm font-medium flex items-center"
              title="Export to CSV"
            >
              <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
              Export
            </button>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} size="lg" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex flex-col h-full">
          {/* Filter Controls */}
          <div className="px-6 py-4 bg-gray-50 border-b space-y-4">
            {/* Performance Bracket Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Performance Category
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleBracketChange("all")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedBracket === "all"
                      ? "bg-blue-600 text-white"
                      : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  All Records ({statistics.all})
                </button>
                <button
                  onClick={() => handleBracketChange("green")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedBracket === "green"
                      ? "bg-green-600 text-white"
                      : "bg-white border border-green-300 text-green-700 hover:bg-green-50"
                  }`}
                >
                  Good (0-72h) ({statistics.green})
                </button>
                <button
                  onClick={() => handleBracketChange("yellow")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedBracket === "yellow"
                      ? "bg-yellow-600 text-white"
                      : "bg-white border border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                  }`}
                >
                  Moderate (73-121h) ({statistics.yellow})
                </button>
                <button
                  onClick={() => handleBracketChange("red")}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedBracket === "red"
                      ? "bg-red-600 text-white"
                      : "bg-white border border-red-300 text-red-700 hover:bg-red-50"
                  }`}
                >
                  Poor (121+h) ({statistics.red})
                </button>
              </div>
            </div>

            {/* Entry Point Filter */}
            {entryPointOptions.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Point
                </label>
                <select
                  value={selectedEntryPoint}
                  onChange={(e) => handleEntryPointChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Entry Points</option>
                  {entryPointOptions.map((ep) => (
                    <option key={ep} value={ep}>
                      Entry Point {ep}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Active Filters Display */}
            <div className="flex items-center text-sm text-gray-600">
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              <span>
                Showing {filteredData.length} records
                {selectedBracket !== "all" && (
                  <span className="ml-1">
                    • {selectedBracket === "green" ? "Good" : selectedBracket === "yellow" ? "Moderate" : "Poor"} performance
                  </span>
                )}
                {selectedEntryPoint !== "all" && (
                  <span className="ml-1">• Entry Point {selectedEntryPoint}</span>
                )}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {[
                    { field: "reference", label: "Reference" },
                    { field: "customer", label: "Customer" },
                    { field: "entryPoint", label: "Entry Point" },
                    { field: "status", label: "Status" },
                    { field: "attentionTime", label: "Attention Time" },
                    { field: null, label: "Performance" },
                  ].map(({ field, label }) => (
                    <th
                      key={label}
                      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                        field ? "cursor-pointer hover:bg-gray-100" : ""
                      }`}
                      onClick={field ? () => handleSort(field) : undefined}
                    >
                      <div className="flex items-center">
                        {label}
                        {field && sortConfig.field === field && (
                          <FontAwesomeIcon
                            icon={faSort}
                            className={`ml-1 ${
                              sortConfig.direction === "asc" ? "rotate-180" : ""
                            }`}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((item, index) => {
                  const performance = getPerformanceIndicator(item.calculatedAttentionTime);
                  return (
                    <tr key={item.header?.reference || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.header?.reference || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {item.header?.customer_name || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {item.header?.pedimento?.pedimento_entry_point || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.header?.detail?.traffic_data?.status === "Pagado"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}>
                          {item.header?.detail?.traffic_data?.status === "Pagado"
                            ? "Paid"
                            : "Open"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {item.calculatedAttentionTime !== null
                          ? `${item.calculatedAttentionTime.toFixed(1)}h`
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={performance.icon}
                            className={`${performance.color} mr-2`}
                          />
                          <span className={`text-sm font-medium ${performance.color}`}>
                            {performance.text}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 bg-gray-50 border-t">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, sortedData.length)} of{" "}
                  {sortedData.length} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillDownModal;